import React from 'react';
import PropTypes from 'prop-types';
import StatusBadge from './StatusBadge';
import './ComplianceCard.css';

/**
 * ComplianceCard Component
 * Displays compliance information in a card format
 *
 * @param {string} icon - Icon/emoji to display
 * @param {string} title - Card title
 * @param {string} description - Card description
 * @param {string} status - Compliance status (e.g., 'twilio-approved', 'pending-review')
 * @param {Array} details - Array of detail objects with { label, value }
 * @param {Array} actions - Array of action objects with { label, onClick, variant }
 * @param {node} children - Custom content
 * @param {boolean} loading - Whether card is in loading state
 * @param {Function} onClick - Make entire card clickable
 */
const ComplianceCard = ({
  icon,
  title,
  description,
  status,
  details = [],
  actions = [],
  children,
  loading = false,
  onClick,
}) => {
  const cardClasses = [
    'compliance-card',
    onClick ? 'compliance-card--clickable' : '',
    loading ? 'compliance-card--loading' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={cardClasses} onClick={onClick}>
      <div className="compliance-card__header">
        <div className="compliance-card__header-left">
          <div className="compliance-card__header-text">
            <h3 className="compliance-card__title">{title}</h3>
              {status && (
            <div className="compliance-card__status">
              <StatusBadge status={status} size="small" />
            </div>
          )}
          </div>
        
        </div>
        {description && (
          <p className="compliance-card__description">{description}</p>
        )}
      </div>

      {details.length > 0 && (
        <div className="compliance-card__details">
          {details.map((detail, index) => (
            <div key={index} className="compliance-card__detail-item">
              <span className="compliance-card__detail-label">{detail.label}:</span>
              <span className="compliance-card__detail-value">{detail.value}</span>
            </div>
          ))}
        </div>
      )}

      {children && <div className="compliance-card__custom">{children}</div>}

      {actions.length > 0 && (
        <div className="compliance-card__actions">
          {actions.map((action, index) => (
            <button
              key={index}
              className="btn linear-gradient yr-submitbtn"
              onClick={(e) => {
                e.stopPropagation();
                action.onClick();
              }}
              disabled={action.disabled}
            >
              {action.label}
            </button>
          ))}
        </div>
      )}

      {loading && (
        <div className="compliance-card__loading-overlay">
          <div className="compliance-card__spinner" />
        </div>
      )}
    </div>
  );
};

ComplianceCard.propTypes = {
  icon: PropTypes.node,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  status: PropTypes.string,
  details: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.node]).isRequired,
    })
  ),
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired,
      variant: PropTypes.oneOf(['primary', 'secondary', 'danger']),
      icon: PropTypes.node,
      disabled: PropTypes.bool,
    })
  ),
  children: PropTypes.node,
  loading: PropTypes.bool,
  onClick: PropTypes.func,
};

export default ComplianceCard;
